import * as yaml from "js-yaml";

// ── Types ────────────────────────────────────────────────────────────────────

export type ServiceType =
  "server" | "static" | "worker" | "stateful" | "scheduler" | "cron";

export interface FormEnvVar {
  name: string;
  value: string;
}

export interface FormService {
  name: string;
  type: ServiceType;
  port?: number;
  internetFacing: boolean;
  dockerfile?: string;
  context?: string;
  image?: string;
  cpu?: string;
  memory?: string;
  storage?: string;
  env: FormEnvVar[];
}

export interface FormState {
  pipelineMode: string;
  services: FormService[];
}

export interface ManifestMetadata {
  name: string;
  project: string;
  org: string;
}

interface ParsedService {
  name?: string;
  type?: string;
  port?: number;
  internetFacing?: boolean;
  image?: string;
  storage?: string;
  resources?: { cpu?: string; memory?: string };
  vcs?: {
    dockerfile?: string;
    context?: string;
    baseImage?: string;
    buildCommand?: string;
    buildDir?: string;
  };
  env?: Array<{ name: string; value?: string; secretKey?: string }>;
}

interface ParsedManifest {
  apiVersion?: string;
  kind?: string;
  metadata?: { name?: string; project?: string; org?: string };
  spec?: {
    pipelineMode?: string;
    services?: ParsedService[];
  };
}

// ── Serialization ───────────────────────────────────────────────────────────

/**
 * Parse a rusha.yml YAML string into form state.
 * Returns { error } if the YAML is syntactically invalid or missing required fields.
 */
export function manifestToFormState(
  yamlContent: string,
): { pipelineMode: string; services: FormService[] } | { error: string } {
  let parsed: unknown;
  try {
    parsed = yaml.load(yamlContent);
  } catch (err) {
    return { error: `YAML parse error: ${(err as Error).message}` };
  }

  if (typeof parsed !== "object" || parsed === null) {
    return { error: "Invalid manifest: expected a YAML object" };
  }

  const manifest = parsed as ParsedManifest;
  if (manifest.kind !== "System") {
    return { error: `Expected kind: System, got: ${manifest.kind ?? "none"}` };
  }

  const spec = manifest.spec;
  if (!spec || !Array.isArray(spec.services)) {
    return { error: "Missing spec.services array" };
  }

  const services: FormService[] = spec.services.map((s) => ({
    name: s.name ?? "",
    type: (s.type as ServiceType) ?? "server",
    ...(s.port !== undefined && { port: s.port }),
    internetFacing: s.internetFacing ?? false,
    ...(s.vcs?.dockerfile && { dockerfile: s.vcs.dockerfile }),
    ...(s.vcs?.context && { context: s.vcs.context }),
    ...(s.image && { image: s.image }),
    ...(s.resources?.cpu && { cpu: s.resources.cpu }),
    ...(s.resources?.memory && { memory: s.resources.memory }),
    ...(s.storage && { storage: s.storage }),
    env: (s.env ?? []).map((e) => ({
      name: e.name,
      value: e.value ?? e.secretKey ?? "",
    })),
  }));

  return {
    pipelineMode: spec.pipelineMode ?? "rusha_managed",
    services,
  };
}

/**
 * Serialize form state back into a complete rusha.yml YAML string.
 * Preserves apiVersion, kind, metadata, and spec structure.
 */
export function formStateToYaml(
  pipelineMode: string,
  services: FormService[],
  metadata: ManifestMetadata,
): string {
  const manifest: Record<string, unknown> = {
    apiVersion: "rusha.io/v1",
    kind: "System",
    metadata: {
      name: metadata.name,
      project: metadata.project,
      org: metadata.org,
    },
    spec: {
      pipelineMode,
      services: services.map((s) => {
        const svc: Record<string, unknown> = {
          name: s.name,
          type: s.type,
          ...(s.port !== undefined && { port: s.port }),
          internetFacing: s.internetFacing,
        };

        if (s.image) svc.image = s.image;
        if (s.storage) svc.storage = s.storage;

        if (s.dockerfile || s.context) {
          svc.vcs = {
            ...(s.dockerfile && { dockerfile: s.dockerfile }),
            ...(s.context && { context: s.context }),
          };
        }

        if (s.cpu || s.memory) {
          svc.resources = {
            ...(s.cpu && { cpu: s.cpu }),
            ...(s.memory && { memory: s.memory }),
          };
        }

        if (s.env.length > 0) {
          svc.env = s.env.map((e) => ({
            name: e.name,
            ...(e.value && { value: e.value }),
          }));
        }

        return svc;
      }),
    },
  };

  return yaml.dump(manifest, {
    lineWidth: -1,
    noRefs: true,
    quotingType: '"',
  });
}

/**
 * Create an empty FormService with sensible defaults.
 */
export function createEmptyService(): FormService {
  return {
    name: "",
    type: "server",
    internetFacing: false,
    env: [],
  };
}

/**
 * Create a FormService from a template ServiceSpec (provisioned stacks).
 */
export function serviceSpecToFormService(spec: {
  name: string;
  type: string;
  port?: number;
  image?: string;
  internetFacing: boolean;
  resources?: { cpu: string; memory: string };
  storage?: string;
}): FormService {
  return {
    name: spec.name,
    type: spec.type as ServiceType,
    ...(spec.port !== undefined && { port: spec.port }),
    internetFacing: spec.internetFacing,
    ...(spec.image && { image: spec.image }),
    ...(spec.resources?.cpu && { cpu: spec.resources.cpu }),
    ...(spec.resources?.memory && { memory: spec.resources.memory }),
    ...(spec.storage && { storage: spec.storage }),
    env: [],
  };
}
